import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PaymentContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const SummaryContainer = styled.div`
  background: #f7fafc;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SummaryTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: #2d3748;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
    font-weight: bold;
  }
`;

const Label = styled.span`
  color: #4a5568;
`;

const Value = styled.span`
  color: #2d3748;
  font-weight: 500;
`;

const Button = styled.button`
  background: #4299e1;
  color: white;
  padding: 1rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3182ce;
  }

  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  background-color: #fff5f5;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

interface PaymentStepProps {
  plan: {
    name: string;
    price: number;
    period?: string;
  };
  onSuccess: () => void;
}

export const PaymentStep: React.FC<PaymentStepProps> = ({ plan, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      
      if (submitError) {
        setError(submitError.message || 'An error occurred with your payment');
        setProcessing(false);
        return;
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/payment-success',
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'An error occurred with your payment');
        setProcessing(false);
        return;
      }

      // If we get here, the payment was successful
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setProcessing(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <SummaryContainer>
        <SummaryTitle>Order Summary</SummaryTitle>
        <SummaryRow>
          <Label>Plan</Label>
          <Value>{plan.name}</Value>
        </SummaryRow>
        <SummaryRow>
          <Label>Price</Label>
          <Value>${plan.price}{plan.period ? `/${plan.period}` : ''}</Value>
        </SummaryRow>
      </SummaryContainer>

      <PaymentContainer>
        <PaymentElement />
      </PaymentContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Button type="submit" disabled={!stripe || processing}>
        {processing ? 'Processing...' : 'Complete Payment'}
      </Button>
    </Form>
  );
}; 